import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import TradesTable from '../../trades/ui/trades-table.jsx';

function AccountFieldsTable({ fields, emptyText }) {
  if (!fields.length) {
    return <div className="text-muted">{emptyText}</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-borderless table-sm fw-semibold m-0 align-middle">
        <tbody>
          {fields.map((field) => (
            <tr key={field.key}>
              <td className="text-muted small pe-3" style={{ width: '42%' }}>{field.label}</td>
              <td className="text-end text-break">
                {field.isJson ? (
                  <pre className="mb-0 small bg-light border rounded p-2 text-start">{field.value}</pre>
                ) : field.badge === 'pendingVerification' ? (
                  <span className="badge rounded-pill bg-warning-subtle text-warning border border-warning-subtle">
                    {field.pendingVerificationLabel}
                  </span>
                ) : field.linkTo ? (
                  <EntityLink to={field.linkTo} className="justify-content-end text-decoration-none">
                    {field.value}
                  </EntityLink>
                ) : (
                  field.value
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountDetailsCard({ iconClass, title, badge, badgeStyle, fields, emptyText }) {
  return (
    <div className="card h-100 border-0">
      <div className="card-header bg-none px-3 py-2 h6 m-0 d-flex align-items-center gap-2">
        <i className={`${iconClass} fa-lg text-gray text-opacity-50`}></i>
        <span className="account-details-card-title">{title}</span>
        <span
          className="ms-auto badge rounded-pill px-3 py-1"
          style={{ fontSize: '0.8rem', ...badgeStyle }}
        >
          {badge || '-'}
        </span>
      </div>
      <div className="card-body p-3">
        <AccountFieldsTable fields={fields} emptyText={emptyText} />
      </div>
    </div>
  );
}

function AccountDetailsPageView({
  tabs,
  activeTab,
  setActiveTab,
  details,
  isLoading,
  error,
  syncingId,
  syncError,
  syncSuccess,
  syncAccount,
  goBack,
  backLabel,
  trades,
  tradesTotal,
  tradesTotalLots,
  tradesTotalProfit,
  tradesProfitCurrency,
  isTradesLoading,
  tradesError,
  tradesSymbolFilter,
  tradesDateFromFilter,
  tradesDateToFilter,
  handleTradesSymbolChange,
  handleTradesDateFromChange,
  handleTradesDateToChange,
  loadMoreTrades,
}) {
  const { t } = useTranslation();
  const isSyncDisabled = Boolean(syncingId) || Boolean(details?.isPendingVerification);

  return (
    <div>
      <FloatingAlerts error={error || syncError} success={syncSuccess} />

      <PageHeader title={t('accounts.detailsTitle')} subtitle="" />

      <div className="mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm me-2" onClick={goBack}>
          <i className="fa fa-arrow-left me-1"></i> {backLabel || t('accounts.backToAccounts')}
        </button>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={syncAccount}
          disabled={isSyncDisabled}
          title={details?.isPendingVerification ? t('accounts.pendingVerificationSyncDisabled') : undefined}
        >
          {syncingId ? t('accounts.syncing') : t('accounts.sync')}
        </button>
      </div>

      <LoadingContainer isLoading={isLoading} hasData={Boolean(details)}>
        <div className="card card-body">
          <ul className="nav nav-tabs flex-nowrap">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <button
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {t(tab.label)}
                </button>
              </li>
            ))}
          </ul>

          <div className="tab-content mt-3">
            <div className={`tab-pane fade ${activeTab === 'details' ? 'active show' : ''}`}>
              {details ? (
                <div className="row row-cols-1 row-cols-xl-3 g-3 align-items-stretch">
                  <div className="col">
                    <AccountDetailsCard
                      iconClass="fa fa-list-check"
                      title={t('accounts.basicAttributes')}
                      badge={details.type}
                      badgeStyle={{ backgroundColor: '#0dcaf0', color: '#052c3b' }}
                      fields={details.basicFields.map((field) => ({
                        ...field,
                        pendingVerificationLabel: t('accounts.pendingVerification'),
                      }))}
                      emptyText={t('common.noData')}
                    />
                  </div>

                  <div className="col">
                    <AccountDetailsCard
                      iconClass="fa fa-credit-card"
                      title={t('accounts.financialInformation')}
                      badge={details.currency}
                      badgeStyle={{ backgroundColor: '#198754', color: '#ffffff' }}
                      fields={details.financeFields}
                      emptyText={t('accounts.financialDataUnavailable')}
                    />
                  </div>

                  <div className="col">
                    <AccountDetailsCard
                      iconClass="fa fa-user"
                      title={t('accounts.profile')}
                      badge={details.profile?.status}
                      badgeStyle={{ backgroundColor: '#6f42c1', color: '#ffffff' }}
                      fields={details.profile?.fields || []}
                      emptyText={t('common.noData')}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className={`tab-pane fade ${activeTab === 'trades' ? 'active show' : ''}`}>
              <TradesTable
                trades={trades}
                total={tradesTotal}
                totalLots={tradesTotalLots}
                totalProfit={tradesTotalProfit}
                profitCurrency={tradesProfitCurrency}
                isLoading={isTradesLoading}
                error={tradesError}
                showAccountLogin={false}
                symbolFilter={tradesSymbolFilter}
                dateFromFilter={tradesDateFromFilter}
                dateToFilter={tradesDateToFilter}
                onSymbolChange={handleTradesSymbolChange}
                onDateFromChange={handleTradesDateFromChange}
                onDateToChange={handleTradesDateToChange}
                onLoadMore={loadMoreTrades}
                hasMore={trades.length < tradesTotal}
              />
            </div>
          </div>
        </div>
      </LoadingContainer>
    </div>
  );
}

export default AccountDetailsPageView;
